import API from 'app/services/company/companyApi'
import { mapCompanyFields } from 'app/actions/company'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'

/**
 * Zapytania firm, wyjęte ze slice'a `companies`.
 *
 * Slice trzymał w jednym worku siedem czytanych pól karmionych osobnymi akcjami:
 * pełną listę, listę uproszczoną, listę dla grup, pojedynczą firmę, jej szczegóły,
 * bieżącą firmę i historię pakietów. Każde dostaje własny klucz — w jednym slice'ie
 * pobranie jednej listy potrafiło nadpisać drugą (`GET_COMPANY_LIST` i
 * `GET_AGGREGATED_COMPANY_LIST` pisały w to samo pole).
 *
 * `mapCompanyFields` jest importowane z `app/actions/company`, nie skopiowane.
 */
export const companiesKey = (companyId?: string | null) =>
  ['admin', 'companies', companyId ?? null] as const

export const companiesSimpleKey = (tenantId?: string | null) =>
  ['admin', 'companiesSimple', tenantId ?? null] as const

export const companiesForGroupsKey = (ids?: string[] | null) =>
  ['admin', 'companiesForGroups', (ids ?? []).join(',')] as const

export const companyKey = (companyId?: string | null) =>
  ['admin', 'company', companyId ?? null] as const

export const currentCompanyKey = (companyId?: string | null) =>
  ['admin', 'currentCompany', companyId ?? null] as const

export const packageHistoryKey = (companyId?: string | null) =>
  ['admin', 'packageHistory', companyId ?? null] as const

const mapList = (data: any[]): CompanyDetailsModel[] =>
  data.map((c) => mapCompanyFields(c))

export const fetchCompanies = async (
  tenantId?: string,
): Promise<CompanyDetailsModel[]> => {
  const response = await API.create().getCompanies(tenantId)
  return mapList(response.content.data)
}

export const fetchCompaniesSimple = async (
  tenantId?: string,
): Promise<CompanyDetailsModel[]> => {
  const response = await API.create().getCompaniesSimple(tenantId)
  return mapList(response.content.data)
}

export const fetchCompaniesForGroups = async (
  ids: string[] = [],
  includeDeleted = false,
): Promise<CompanyDetailsModel[]> => {
  const response = await API.create().getCompaniesSimpleForGroups(
    ids,
    includeDeleted,
  )
  return mapList(response.content.data)
}

export const fetchCompany = async (
  companyId: string,
  ignoreMigration?: boolean,
): Promise<CompanyDetailsModel | null> => {
  const response = await API.create().getCompany(companyId, ignoreMigration)
  const data = response.content.data
  return data !== '' ? mapCompanyFields(data) : null
}

export const fetchCurrentCompany = async (companyId: string) => {
  const response = await API.create().getCurrentCompany(companyId)
  return response.content.data
}

export const fetchPackageHistory = async (companyId: string) => {
  const response = await API.create().getPackageHistory(companyId)
  return response.content.data
}
